/**
 * Map paginated CheckpointVersion history API rows to CheckpointVersionHistoryPanel props.
 *
 * API: GET .../checkpoint/<id>/versions/
 * Panel expects camelCase display rows (see CheckpointVersionHistoryPanel.vue props).
 */

function getPublishedBy(api = {}) {
  if (api.published_by && typeof api.published_by === "object") {
    return api.published_by;
  }
  if (api.published_by_user_obj && typeof api.published_by_user_obj === "object") {
    return api.published_by_user_obj;
  }
  return null;
}

function publishedByName(api = {}) {
  if (api.publisher_name) return api.publisher_name;
  const by = getPublishedBy(api);
  if (!by) return undefined;
  if (by.name) return by.name;
  const first = by.first_name || by.firstName || "";
  const last = by.last_name || by.lastName || "";
  const full = `${first} ${last}`.trim();
  return full || undefined;
}

function isRetiredSupersededReason(reason) {
  return String(reason ?? "").toUpperCase() === "RETIRED";
}

/**
 * @param {object} api — single history row from the versions list API
 * @param {object} [options]
 * @param {string} [options.checkpointStatus] — parent checkpoint status (e.g. retired)
 * @returns {object} display row for CheckpointVersionHistoryPanel
 */
export function mapCheckpointVersionHistoryRow(api = {}, options = {}) {
  const publishedBy = getPublishedBy(api);
  const checkpointStatus = String(options.checkpointStatus ?? "").toLowerCase();
  const versionRetired = isRetiredSupersededReason(api.superseded_reason);
  const isRetired =
    versionRetired || (checkpointStatus === "retired" && !!api.is_current);

  return {
    id: api.id,
    versionNumber: api.version_number,
    publishedAt: api.published_at,
    publisherName: publishedByName(api),
    publisherFirstName:
      api.publisher_first_name || publishedBy?.first_name || publishedBy?.firstName || undefined,
    publisherLastName:
      api.publisher_last_name || publishedBy?.last_name || publishedBy?.lastName || undefined,
    publisherAvatar:
      api.publisher_avatar ?? publishedBy?.avatar ?? publishedBy?.picture ?? null,
    isCurrent: !!api.is_current && !isRetired,
    isRetired,
  };
}

/**
 * @param {object[]|undefined|null} apiRows
 * @param {object} [options] — forwarded to {@link mapCheckpointVersionHistoryRow}
 * @returns {object[]}
 */
export function mapCheckpointVersionHistoryRows(apiRows, options = {}) {
  if (!Array.isArray(apiRows)) return [];

  const rows = apiRows.map((row) => mapCheckpointVersionHistoryRow(row, options));
  const checkpointRetired = String(options.checkpointStatus ?? "").toLowerCase() === "retired";

  if (checkpointRetired && rows.length) {
    const retiredRow = rows.find((row) => row.isRetired) ?? rows[0];
    retiredRow.isRetired = true;
    retiredRow.isCurrent = false;
  }

  return rows;
}

const PROFICIENCY_LEVEL_NAMES = {
  1: "Apprentice",
  2: "Professional",
  3: "Coach",
  4: "Master",
};

const READINESS_LEVEL_NAMES = {
  APPRENTICE: "Apprentice",
  PROFESSIONAL: "Professional",
  COACH: "Coach",
  MASTER: "Master",
};

function resolveLevelName(api = {}) {
  const fromProficiency = PROFICIENCY_LEVEL_NAMES[api.proficiency_level];
  if (fromProficiency) return fromProficiency;
  const readiness = String(api.readiness_level ?? "").toUpperCase();
  if (READINESS_LEVEL_NAMES[readiness]) return READINESS_LEVEL_NAMES[readiness];
  return "Apprentice";
}

function pickPopulated(...values) {
  for (const value of values) {
    if (value == null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    if (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) {
      continue;
    }
    return value;
  }
  return null;
}

/**
 * Map a CheckpointVersion history API row to the checkpoint shape consumed by
 * CheckpointPastVersionCard / CheckpointEditFormV2 `pastVersionView`.
 *
 * @param {object} api — full version snapshot from the versions list API
 * @param {number|string|null} checkpointId — parent checkpoint id (not version id)
 * @returns {object}
 */
export function mapCheckpointVersionSnapshotToCheckpoint(api = {}, checkpointId = null) {
  const id = checkpointId ?? api.checkpoint_id ?? api.id;
  const microlearningInstructions = pickPopulated(
    api.microlearning_instructions,
    api.active_video_microlearning,
    api.video_microlearning,
    api.active_inspection_instructions
  );

  return {
    id,
    level: resolveLevelName(api),
    criteria: api.criteria ?? null,
    prefix: api.prefix ?? null,
    status: api.status ?? "active",
    proficiency_level: api.proficiency_level ?? null,
    readiness_level: api.readiness_level ?? null,
    instructions: api.instructions ?? null,
    microlearning: api.microlearning ?? api.active_microlearning ?? null,
    microlearning_video_id: api.microlearning_video_id ?? api.active_microlearning_video_id ?? null,
    microlearning_cloudflare_video_id:
      api.microlearning_cloudflare_video_id ?? api.active_microlearning_cloudflare_video_id ?? null,
    microlearning_video_thumbnail:
      api.microlearning_video_thumbnail ?? api.active_microlearning_video_thumbnail ?? null,
    microlearning_instructions: microlearningInstructions,
    quiz_mode: api.active_quiz_mode ?? api.quiz_mode ?? null,
    active_microlearning_type: api.active_microlearning_type ?? api.microlearning_type ?? null,
    active_article: api.active_article ?? api.article ?? null,
    active_quiz: api.active_quiz ?? api.quiz ?? null,
    active_quiz_autopass: api.active_quiz_autopass ?? null,
    active_quiz_mode: api.active_quiz_mode ?? api.quiz_mode ?? null,
    active_video_microlearning: api.active_video_microlearning ?? api.video_microlearning ?? null,
    active_inspection_protocol: api.active_inspection_protocol ?? api.inspection_protocol ?? null,
    active_inspection_type: api.active_inspection_type ?? api.inspection_type ?? null,
    active_inspection_instructions: api.active_inspection_instructions ?? null,
    active_tutorial: api.active_tutorial ?? null,
    active_video_id: api.active_video_id ?? null,
    active_apivideo_id: api.active_apivideo_id ?? null,
    activecloudflarevideo_id: api.activecloudflarevideo_id ?? null,
    active_video_source: api.active_video_source ?? api.video_source ?? null,
    active_video_source_thumbnail:
      api.active_video_source_thumbnail ?? api.video_source_thumbnail ?? null,
    active_qr_code: api.active_qr_code ?? api.qr_code ?? null,
    active_microlearning: api.active_microlearning ?? api.microlearning ?? null,
    active_microlearning_video_id:
      api.active_microlearning_video_id ?? api.microlearning_video_id ?? null,
    active_microlearning_cloudflare_video_id:
      api.active_microlearning_cloudflare_video_id ??
      api.microlearning_cloudflare_video_id ??
      null,
    active_microlearning_video_thumbnail:
      api.active_microlearning_video_thumbnail ?? api.microlearning_video_thumbnail ?? null,
    active_has_inspection_protocols: api.active_has_inspection_protocols ?? null,
    proposed_microlearning_type: null,
    lock_checkpoint: true,
  };
}
